import { useContext, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query'
import FetchContext from '../../context/fetch/FetchContext'
import { refusalOf, wordingOf } from '../forms/refusal'

const JSON_HEADERS = { 'Content-Type': 'application/json' }

const MUTATION_ROOT = 'optimistic-list'

function nextPosition(items) {
    const positions = items.map((item) => item.position ?? -1)
    return Math.max(-1, ...positions) + 1
}

function withItem(items, id, item) {
    return items.map((current) => current.id === id ? item : current)
}

function withoutItem(items, id) {
    return items.filter((current) => current.id !== id)
}

export default function useOptimisticList({
    queryKey,
    lens,
    path,
    positionsPath,
    scope,
    toInput = (item) => item,
    labelOf,
    onRefused,
    onStored,
}) {
    const fetcher = useContext(FetchContext)
    const queryClient = useQueryClient()
    const nextTemporaryId = useRef(0)
    const [removals, setRemovals] = useState([])
    const [refusals, setRefusals] = useState({})

    const queryCache = queryClient.getQueryCache()
    const data = useSyncExternalStore(
        (notify) => queryCache.subscribe(notify),
        () => queryClient.getQueryData(queryKey),
    )

    const mutationKey = [MUTATION_ROOT, scope]

    const storingIds = useMutationState({
        filters: {
            mutationKey,
            status: 'pending',
        },
        select: (mutation) => mutation.state.variables?.itemId,
    })

    const readItems = () => {
        const cached = queryClient.getQueryData(queryKey)
        return lens.get(cached)
    }

    const writeItems = (update) => {
        queryClient.cancelQueries({ queryKey })
        queryClient.setQueryData(queryKey, (cached) => {
            if (cached == null) {
                return cached
            }

            const current = lens.get(cached)
            const updated = update(current)
            return lens.set(cached, updated)
        })
    }

    const showRefusalOn = (id, notices) => {
        const first = notices[0]
        const wording = wordingOf(first.code, first.params)

        setRefusals((current) => ({
            ...current,
            [id]: wording,
        }))
    }

    const clearRefusalOf = (id) => {
        setRefusals(({ [id]: _cleared, ...rest }) => rest)
    }

    const refused = (error, { input, itemId }) => {
        const { notices, fieldErrors } = refusalOf(error, { labelOf })

        if (itemId != null) {
            showRefusalOn(itemId, notices)
        }

        onRefused?.(error)

        return {
            refused: error,
            input,
            fieldErrors,
        }
    }

    const mutationOptions = {
        mutationKey,
        scope: { id: scope },
    }

    const createMutation = useMutation({
        ...mutationOptions,
        mutationFn: ({ input }) => fetcher.authenticatedFetch({
            method: 'post',
            path,
            data: { data: input },
            headers: JSON_HEADERS,
        }),
        onSuccess: (stored, { itemId }) => {
            writeItems((items) => withItem(items, itemId, stored))
        },
        onError: (_error, { itemId }) => {
            writeItems((items) => withoutItem(items, itemId))
        },
    })

    const updateMutation = useMutation({
        ...mutationOptions,
        mutationFn: ({ itemId, input }) => fetcher.authenticatedFetch({
            method: 'put',
            path: `${path}/${itemId}`,
            data: { data: input },
            headers: JSON_HEADERS,
        }),
        onSuccess: (stored, { itemId }) => {
            writeItems((items) => withItem(items, itemId, stored ?? items.find((item) => item.id === itemId)))
        },
        onError: (_error, { itemId, previous }) => {
            writeItems((items) => withItem(items, itemId, previous))
        },
    })

    const removeMutation = useMutation({
        ...mutationOptions,
        mutationFn: ({ itemId }) => fetcher.authenticatedFetch({
            method: 'delete',
            path: `${path}/${itemId}`,
        }),
        onSuccess: (_stored, { itemId }) => {
            writeItems((items) => withoutItem(items, itemId))
            setRemovals((current) => current.filter((id) => id !== itemId))
        },
    })

    const positionsMutation = useMutation({
        ...mutationOptions,
        mutationFn: ({ positions }) => fetcher.authenticatedFetch({
            method: 'put',
            path: positionsPath,
            data: { data: positions },
            headers: JSON_HEADERS,
        }),
        onError: (_error, { previous }) => {
            writeItems(() => previous)
        },
    })

    const settle = async (mutation, variables) => {
        try {
            const stored = await mutation.mutateAsync(variables)
            await onStored?.()
            return { stored }
        }
        catch (error) {
            const itemId = variables.created ? null : variables.itemId
            return refused(error, {
                input: variables.input,
                itemId,
            })
        }
    }

    const create = (input) => {
        nextTemporaryId.current += 1
        const itemId = `new-${nextTemporaryId.current}`

        writeItems((items) => {
            const added = {
                ...input,
                id: itemId,
                position: nextPosition(items),
            }
            return [...items, added]
        })

        return settle(createMutation, {
            itemId,
            input,
            created: true,
        })
    }

    const update = (itemId, changes) => {
        const previous = readItems().find((item) => item.id === itemId)
        const merged = {
            ...previous,
            ...changes,
        }

        clearRefusalOf(itemId)
        writeItems((items) => withItem(items, itemId, merged))

        return settle(updateMutation, {
            itemId,
            input: toInput(merged),
            previous,
        })
    }

    const remove = (itemId) => {
        clearRefusalOf(itemId)
        setRemovals((current) => current.includes(itemId) ? current : [...current, itemId])
    }

    const restore = (itemId) => {
        clearRefusalOf(itemId)
        setRemovals((current) => current.filter((id) => id !== itemId))
    }

    const confirmRemoval = (itemId) => {
        clearRefusalOf(itemId)

        return settle(removeMutation, { itemId })
    }

    const reorder = (ids) => {
        const previous = readItems()
        const positions = ids.map((id, position) => ({
            id,
            position,
        }))
        const positionById = new Map(positions.map((entry) => [entry.id, entry.position]))

        writeItems((items) => items.map((item) => positionById.has(item.id)
            ? {
                ...item,
                position: positionById.get(item.id),
            }
            : item))

        return settle(positionsMutation, {
            positions,
            previous,
        })
    }

    const setItems = (update) => {
        writeItems(update)
    }

    const cachedItems = lens.get(data)

    const items = useMemo(
        () => cachedItems.map((item) => ({
            ...item,
            storing: storingIds.includes(item.id),
            removed: removals.includes(item.id),
        })),
        [cachedItems, storingIds, removals],
    )

    return {
        items,
        removals,
        refusals,
        storing: storingIds.length > 0,
        create,
        update,
        remove,
        restore,
        confirmRemoval,
        reorder,
        setItems,
    }
}
